import JsonView from "@uiw/react-json-view"
import clsx from "clsx"
import { FC, Fragment } from "react"
import { useTranslation } from "react-i18next"
import { useGameLogic } from "@/hooks/useGameLogic"
import { useModal } from "@/modals"
import { useAppSelector } from "@/store"
import { IActiveEffect } from "@/store/apis/effectsAPI"
import { AppSelectors } from "@/store/slices/app"
import { Button } from "@/ui/Button"
import { EffectFrame } from "@/ui/EffectFrame"
import { Modal } from "@/ui/Modal"
import { OutlinedText } from "@/ui/OutlinedText"
import styles from "./MatchEffectsModal.module.scss"

const modalType = "matchEffects"

export const MatchEffectsModal: FC = () => {
  const { t } = useTranslation()
  const { modal, getModalParams } = useModal(modalType)
  const { getActiveEffectsByPlayerId } = useGameLogic()

  const isDevMode = useAppSelector(AppSelectors.selectIsDevMode())

  const { playerId } = getModalParams(modalType)

  const activeEffects = getActiveEffectsByPlayerId(playerId)

  const handleClose = () => {
    modal.close()
  }

  const description = (effect: IActiveEffect) => {
    let value = effect.effect.value
    const valueAbs = Math.abs(value)
    const duration = effect.effect.durationMovements
    const turnWord = t("turn", { count: duration })

    switch (effect.effect.type) {
      case "auto_dig":
        value = -value
        return t(`effect_types.${effect.effect.type}.description`, {
          value: value > 0 ? `+${valueAbs}` : `-${valueAbs}`,
          duration,
          turnWord
        })
      default:
        return t(`effect_types.${effect.effect.type}.description`, {
          value: value > 0 ? `+${valueAbs}` : `-${valueAbs}`,
          duration,
          turnWord
        })
    }
  }

  return (
    <Modal.Center
      type={modalType}
      className={clsx(styles.MatchEffectsModal)}
      body={{ className: styles.MatchEffectsModal_body }}
    >
      <Modal.StoneFlexContainer
        className={styles.MatchEffectsModal_container}
        content={{ className: styles.MatchEffectsModal_content }}
        outside={
          <>
            <div className={clsx(styles.MatchEffectsModal_footerButtons)}>
              <Button
                className={clsx(styles.MatchEffectsModal_footerButtons_item)}
                styles="green"
                onClick={handleClose}
              >
                {t("button.close")}
              </Button>
            </div>
          </>
        }
      >
        <OutlinedText className={clsx(styles.MatchEffectsModal_title)}>
          {t("effects.title")}
        </OutlinedText>
        <div className={clsx(styles.MatchEffectsModal_effects)}>
          {activeEffects.length === 0 && (
            <div className={styles.MatchEffectsModal_effects_empty}>
              {t("effects.empty")}
            </div>
          )}
          {activeEffects.map((effect, i) => {
            return (
              <Fragment key={effect._id}>
                {i > 0 && (
                  <div className={clsx(styles.MatchEffectsModal_separator)} />
                )}
                <div className={clsx(styles.MatchEffectsModal_effect)}>
                  <EffectFrame
                    className={clsx(styles.MatchEffectsModal_effect_img)}
                    effect={effect}
                  />
                  <div
                    className={clsx(styles.MatchEffectsModal_effect_content)}
                  >
                    <OutlinedText
                      className={clsx(styles.MatchEffectsModal_effect_title)}
                    >
                      {t(`effect_types.${effect.effect.type}.title`)}
                    </OutlinedText>
                    <div
                      className={clsx(
                        styles.MatchEffectsModal_effect_description
                      )}
                    >
                      {description(effect)}
                      {/* {t(`effect_types.${effect.effect.type}.description`)} */}
                    </div>
                  </div>
                </div>
              </Fragment>
            )
          })}
        </div>
        {isDevMode && <JsonView value={activeEffects} collapsed={true} />}
      </Modal.StoneFlexContainer>
    </Modal.Center>
  )
}
